import { useLayoutEffect, useState, useRef } from "react";
import { bylineInitials, bylineText } from "@/features/auth/impersonation/authorByline";
import { readActorSession } from "@/features/auth/impersonation/impersonationStorage";
import {
  Card,
  CardContent,
  CardTitle,
} from "@/components/ui/library/card";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/library/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Ban, CornerDownLeft, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { getRelativeTime } from "@/utils/date";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@/stores/hook";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useNoteMutation } from "@/hooks/api/useNoteMutation";
import FormattedText from "@/components/common/FormattedText";

/**
 * WHATSAPP-STYLE INPUTS: a note box grows with its text (typed or pasted) up
 * to a cap, then scrolls. Sized in JS — CSS `field-sizing: content` is
 * Chromium-only. The composer caps at ~8 lines; the editor may take half the
 * viewport so a long structured note opens readable, not as a two-line slit.
 */
const COMPOSER_MAX_PX = 180;
const editorMaxPx = () => Math.max(180, Math.round(window.innerHeight * 0.5));

function autoGrow(el: HTMLTextAreaElement | null, maxPx: number) {
  if (!el) return;
  el.style.height = "auto";
  // scrollHeight excludes the borders; border-box height must include them.
  const full = el.scrollHeight + (el.offsetHeight - el.clientHeight);
  el.style.height = `${Math.min(full, maxPx)}px`;
  el.style.overflowY = full > maxPx ? "auto" : "hidden";
}

export default function NoteSection() {
  const { notes: newNotes, addNote, applyNoteUpdate } = useActiveDataMutation();
  const { editNote, deleteNote } = useNoteMutation();
  const [inputValue, setInputValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Inline edit state — keyed by the RHF field key so the merged list
  // (lead + child notes) can't mis-target a row.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  // Note pending the delete confirmation dialog.
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  const editRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    autoGrow(textareaRef.current, COMPOSER_MAX_PX);
  }, [inputValue]);
  useLayoutEffect(() => {
    autoGrow(editRef.current, editorMaxPx());
  }, [editValue, editingKey]);
  // Opening a note for edit puts the caret at the END, like WhatsApp.
  useLayoutEffect(() => {
    const el = editRef.current;
    if (!editingKey || !el) return;
    const end = el.value.length;
    el.setSelectionRange(end, end);
    el.scrollTop = el.scrollHeight;
  }, [editingKey]);

  const {
    currentUser: { user },
  } = useAppSelector((state) => state.auth);

  const canSubmit = inputValue.trim().length > 0;

  /** Save the composer's note — Enter key and the ↵ button share this. */
  const submitNote = () => {
    if (!canSubmit) return;

    const newNoteObj = {
      note: inputValue,
      // The note is OWNED by the session account, switched or not — that is
      // what the server writes and what the edit/delete gate below compares.
      createdById: user.id,
      createdAt: new Date().toISOString(),
      createdByName: user.name,
      // …but if this session is a "Switch user" view, the person typing is
      // the parked manager, so the optimistic row carries their name too
      // rather than flipping to it only after the refetch.
      switchedByName: readActorSession()?.user.name ?? null,
    };

    // A COMMENT THE USER WROTE IS NEVER DROPPED (2026-09-30). The composer
    // used to clear itself no matter what: a note refused because the lead /
    // deal was still loading, or one whose save failed, took the typed words
    // with it and had to be written again from memory. Now a refusal leaves
    // the text where it is, and a failed save hands it back — above anything
    // typed since, so neither is lost.
    const text = inputValue;
    const accepted = addNote(newNoteObj, {
      onFailed: () =>
        setInputValue((current) =>
          current.trim() ? `${text}\n${current}` : text
        ),
    });
    if (accepted === false) return;
    setInputValue("");
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitNote();
    }
  };

  const startEdit = (field: any) => {
    setEditingKey(field.id);
    setEditValue(field.note);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue("");
  };

  const saveEdit = (field: any) => {
    const content = editValue.trim();
    if (!content || editNote.isPending) return;
    if (content === field.note) {
      cancelEdit();
      return;
    }
    editNote.mutate(
      { id: field.noteId, content },
      {
        onSuccess: () => {
          applyNoteUpdate(field, {
            note: content,
            editedAt: new Date().toISOString(),
          });
          cancelEdit();
        },
      }
    );
  };

  const confirmDelete = () => {
    if (!deleteTarget || deleteNote.isPending) return;
    deleteNote.mutate(
      { id: deleteTarget.noteId },
      {
        onSuccess: () => {
          applyNoteUpdate(deleteTarget, { isDeleted: true });
          setDeleteTarget(null);
        },
      }
    );
  };

  const hasNewNotes = Boolean(newNotes?.length);
  const isEmpty = !hasNewNotes;

  return (
    <Card className="gap-0 overflow-visible border-[#e6e8e7] rounded-[12px] py-0 shadow-none">
      {/* Sticks under the drawer header + tabs (offset via --view-lead-drawer-sticky-h from ViewLead). */}
      <div
        className="sticky z-[12] -mx-3 border-b border-[#e6e8e7] bg-[#F8F8F8] px-3 pb-2.5 pt-2.5"
        style={{
          top: "var(--view-lead-drawer-sticky-h, 0px)",
        }}
      >
        <CardTitle className="pb-2 text-[14px] font-medium leading-normal tracking-[-0.28px] text-black">
          Notes
        </CardTitle>
        {/* Visible ↵ button next to the Enter shortcut: touch / tablet users
            have no reliable Enter, and nothing showed that typing alone
            was enough. Same save path as the key; Shift+Enter still breaks
            the line. */}
        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a note... (Enter to save · Shift+Enter for a new line)"
            rows={1}
            className="min-h-[40px] resize-none rounded-[10px] border-[#cdd1d0] py-2 pr-[4.75rem] text-[13px] leading-[1.45] tracking-[-0.26px] text-[#697571] focus-visible:ring-1 focus-visible:ring-[#1e302a]"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={submitNote}
            disabled={!canSubmit}
            aria-label="Save note"
            title="Save note (Enter)"
            className={cn(
              "note-save-pill absolute bottom-1.5 right-1.5 flex h-7 select-none items-center gap-1 rounded-full pl-2.5 pr-2 text-[11px] font-semibold tracking-[-0.01em] outline-none",
              canSubmit
                ? "note-save-pill--ready bg-[linear-gradient(135deg,#6fb99c_0%,#156548_100%)] text-white shadow-[0_2px_8px_rgba(21,101,72,0.32),inset_0_1px_0_rgba(255,255,255,0.28)] hover:-translate-y-px hover:shadow-[0_5px_16px_rgba(21,101,72,0.42),inset_0_1px_0_rgba(255,255,255,0.28)] active:translate-y-0 active:scale-95"
                : "cursor-not-allowed border border-dashed border-[#cdd1d0] bg-white text-[#b3bab7]"
            )}
          >
            <span>Enter</span>
            <CornerDownLeft
              className="note-save-pill__icon size-3"
              strokeWidth={2.75}
              aria-hidden
            />
          </button>
        </div>
      </div>

      <CardContent className="flex flex-col gap-2 px-3 pb-3 pt-3">
        {isEmpty && (
          <div
            onClick={() => textareaRef.current?.focus()}
            className="group flex cursor-pointer items-center justify-center rounded-[8px] border border-dashed border-[#e6e8e7] py-3 transition-colors hover:bg-gray-50"
          >
            <p className="text-[12px] text-[#9ca3af] transition-colors group-hover:text-[#697571]">
              No notes yet.{" "}
              <span className="underline decoration-dotted underline-offset-2">
                Start typing...
              </span>
            </p>
          </div>
        )}

        {!isEmpty && (
          <div className="flex flex-col gap-3 px-2">
            {hasNewNotes &&
              newNotes.map((field: any) => {
                // Author-only controls — and only for notes that already have
                // a DB id (a just-typed note gets its id on the next refetch).
                const canModify =
                  !field.isDeleted &&
                  field.noteId != null &&
                  String(field.createdById) === String(user.id);
                const isEditing = editingKey === field.id;

                return (
                  <div
                    key={field.id ?? field.createdAt}
                    className="group flex items-start gap-3 border-b border-[#e6e8e7] pb-3"
                  >
                    <Avatar className="size-8 shrink-0 bg-blue-100">
                      <AvatarFallback className="text-[14px] font-medium text-blue-800">
                        {field.switchedByName
                          ? bylineInitials(field.createdByName, {
                              name: field.switchedByName,
                            })
                          : "ME"}
                      </AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex-1">
                      {field.isDeleted ? (
                        <p className="flex items-center gap-1.5 text-[14px] italic leading-[1.4] text-[#9ca3af]">
                          <Ban className="size-3.5 shrink-0" />
                          This message was deleted
                        </p>
                      ) : isEditing ? (
                        <div className="flex flex-col gap-1.5">
                          <Textarea
                            ref={editRef}
                            autoFocus
                            rows={1}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                saveEdit(field);
                              } else if (e.key === "Escape") {
                                cancelEdit();
                              }
                            }}
                            className="min-h-[40px] resize-none rounded-[10px] border-[#cdd1d0] py-2 text-[13px] leading-[1.45] text-[#1e302a] focus-visible:ring-1 focus-visible:ring-[#1e302a]"
                          />
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => saveEdit(field)}
                              disabled={editNote.isPending}
                              className="rounded-full bg-[#008069] px-3 py-1 text-[12px] font-medium text-white hover:bg-[#006d59] disabled:opacity-60"
                            >
                              {editNote.isPending ? "Saving…" : "Save"}
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="text-[12px] font-medium text-[#697571] hover:text-[#374741]"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // WhatsApp-style: lines kept as typed, *bold* _italic_ ~strike~ `code`.
                        <FormattedText
                          text={field.note}
                          className="text-[14px] leading-[1.4] text-[#1e302a]"
                        />
                      )}
                      {!isEditing && (
                        <p
                          className="text-[12px] text-[#697571]"
                          title={
                            field.switchedByName
                              ? `${field.switchedByName} wrote this while viewing as ${field.createdByName}`
                              : undefined
                          }
                        >
                          – {bylineText(field.createdByName, field.switchedByName ? { name: field.switchedByName } : null)}
                        </p>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      <span className="whitespace-nowrap text-[12px] text-[#9ca3af]">
                        {!field.isDeleted && field.editedAt && (
                          <span className="italic">edited · </span>
                        )}
                        {getRelativeTime(field.createdAt)}
                      </span>
                      {canModify && !isEditing && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              aria-label="Note options"
                              className="rounded p-0.5 text-[#9ca3af] opacity-0 transition-opacity hover:bg-gray-100 hover:text-[#374741] focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
                            >
                              <MoreVertical className="size-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-32">
                            <DropdownMenuItem onClick={() => startEdit(field)}>
                              <Pencil className="size-3.5" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => setDeleteTarget(field)}
                            >
                              <Trash2 className="size-3.5" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </CardContent>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <DialogContent className="sm:max-w-[420px] rounded-xl">
          <DialogHeader>
            <p className="text-base font-semibold text-[#051912]">
              Delete note?
            </p>
            <Separator />
            <DialogDescription className="pt-2 text-[13px] text-[#505e59]">
              The note will be removed for everyone and replaced with “This
              message was deleted”.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex justify-end gap-2">
            <Button
              variant="cancel"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteNote.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirmDelete}
              disabled={deleteNote.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteNote.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
